import { Widget } from '@/components/widget';
import { useUserConfig } from '@/hooks/user/use-user-config';
import { useUserOrganisationUsers } from '@/hooks/user/use-user-organisation-users';
import { useUserDisable } from '@/hooks/user/use-user-disable';
import { useUserEnable } from '@/hooks/user/use-user-enable';
import { useUserSendResetPasswordEmail } from '@/hooks/user/use-user-send-reset-password-email';
import {
  ChevronDoubleLeftIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDoubleRightIcon,
  KeyIcon,
  NoSymbolIcon,
  ShieldExclamationIcon
} from '@heroicons/react/20/solid';
import * as Dialog from '@radix-ui/react-dialog';
import { useEffect, useState } from 'react';
import { useOrganisationUserPermissions } from '@/hooks/user/use-organisation-user-permissions';
import { useOrganisationUserPermissionsUpdate } from '@/hooks/user/use-organisation-user-permissions-update';
import { set } from 'lodash';
import { useEmitter } from '@/hooks/use-emitter';
import { Button } from '@/components/button';
import { Dragabble } from '@/components/drag-drop';
import { LinkIcon, TableCellsIcon } from '@heroicons/react/24/outline';
import { useScreenId } from '@/hooks/use-screen-id';
import { useTabId } from '@/hooks/use-tab-id';

export function UsersWidget({
  id = 'users'
} : {
  id?: string;
}) {
  const { data: userConfigData } = useUserConfig();
  const { data: usersData } = useUserOrganisationUsers();
  const { mutate: enableUser } = useUserEnable();
  const { mutate: disableUser } = useUserDisable();
  const { mutate: sendResetPasswordEmail } = useUserSendResetPasswordEmail();
  const { mutateAsync: getOrganisationUserPermissions } = useOrganisationUserPermissions();
  const [permissions, setPermissions] = useState<string[]>([]);
  const [originalPermissions, setOriginalPermissions] = useState<string[]>([]);
  const { mutateAsync: updateOrganisationUserPermissions } = useOrganisationUserPermissionsUpdate();
  const tabId = useTabId();
  const screenId = useScreenId();
  const idPrefix = `${tabId}/${screenId}`;

  const emit = useEmitter({
    id: `${idPrefix}/${id}`,
    listenersPrefix: idPrefix,
  });

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
    pages: 0,
  });

  useEffect(() => {
    if (usersData?.users) {
      setPagination((prev) => ({
        ...prev,
        pages: Math.ceil(usersData.users.length / prev.pageSize),
      }));
    }
  }, [usersData]);

  const handleFirstPage = () => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const handlePreviousPage = () => {
    setPagination((prev) => ({
      ...prev,
      pageIndex: Math.max(0, prev.pageIndex - 1),
    }));
  };

  const handleNextPage = () => {
    setPagination((prev) => ({
      ...prev,
      pageIndex: Math.min(prev.pages - 1, prev.pageIndex + 1),
    }));
  };

  const handleLastPage = () => {
    setPagination((prev) => ({ ...prev, pageIndex: prev.pages - 1 }));
  };

  const paginatedUsers = usersData?.users?.slice(
    pagination.pageIndex * pagination.pageSize,
    (pagination.pageIndex + 1) * pagination.pageSize
  );

  const handleUserDisableToggle = (user: any) => {
    if (user.enabled) {
      disableUser({ userUuid: user.id });
    } else {
      enableUser({ userUuid: user.id });
    }
  };

  const handleSendResetPasswordEmail = (user: any) => {
    sendResetPasswordEmail({ userUuid: user.id });
  };

  const handlePermissions = (user: any) => {
    const messages = [
      {
        type: 'UserPermissions',
        data: [
          {
            user,
          }
        ]
      },
    ];

    emit(messages);
  }

  return (
    <Widget 
      id={id} 
      title="Users"
      toolbar={
        <>
          <Dragabble value={{ widgetId: id }} type="link">
            <Button>
              <LinkIcon className="h-4 w-4" aria-hidden="true" />
            </Button>
          </Dragabble>
        </>
      }
    >
      <ul role="list" className="divide-y divide-gray-100">
        {paginatedUsers?.map((user) => (
          <li key={user.username} className="flex justify-between gap-x-6 py-5">
            <div className="flex min-w-0 gap-x-4">
              <div className="relative inline-flex items-center justify-center w-10 h-10 overflow-hidden bg-indigo-500 rounded-full">
                <span className="font-medium text-white">
                  {user.firstName?.charAt(0) ?? 'S'}
                  {user.lastName?.charAt(0) ?? 'A'}
                </span>
              </div>
              <div className="min-w-0 flex-auto">
                <p className="text-sm font-semibold leading-6 text-gray-900 flex gap-2">
                  {user.enabled ? (
                    <div className="flex items-center gap-x-1.5" title="Active">
                      <div className="flex-none rounded-full bg-emerald-500/20 p-1">
                        <div className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                      </div>
                    </div>
                    ) : (
                    <div className="flex items-center gap-x-1.5" title="Blocked">
                      <div className="flex-none rounded-full bg-red-500/20 p-1">
                        <div className="h-1.5 w-1.5 rounded-full bg-red-500" />
                      </div>
                    </div>
                  )}
                  {user.firstName ?? 'Service'} {user.lastName ?? 'Account'}
                </p>
                <p className="mt-1 truncate text-xs leading-5 text-gray-500">
                  {user.username}
                </p>
              </div>
            </div>
            <div>
              <span className="inline-flex rounded-md shadow-sm">
                {/* reset password */}
                <button
                  type="button"
                  className="relative inline-flex items-center rounded-l-md bg-white px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10"
                  title="Send Reset Password Email"
                  onClick={() => handleSendResetPasswordEmail(user)}
                >
                  <KeyIcon aria-hidden="true" className="-ml-0.5 h-4 w-4 text-gray-400" />
                </button>
                {/* permissions dialog trigger (shield icon) */}
                <button
                  type="button"
                  className="relative -ml-px inline-flex items-center bg-white px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10"
                  title="Permissions"
                  onClick={() => handlePermissions(user)}
                >
                  <ShieldExclamationIcon
                    aria-hidden="true"
                    className="-ml-0.5 h-4 w-4 text-gray-400"
                  />
                </button>
                {/* block/unblock */}
                <button
                  type="button"
                  className="relative -ml-px inline-flex items-center rounded-r-md bg-white px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10"
                  title={user.enabled ? 'Block User' : 'Unblock User'}
                  onClick={() => handleUserDisableToggle(user)}
                >
                  <NoSymbolIcon aria-hidden="true" className="-ml-0.5 h-4 w-4 text-gray-400" />
                </button>
              </span>
            </div>
          </li>
        ))}
      </ul>

      <div className="flex items-center justify-between border-t border-gray-200 px-4 py-4 sm:px-0">
        <p className="text-sm text-gray-700">
          Page <span className="font-medium">{pagination.pageIndex + 1}</span> of{' '}
          <span className="font-medium">{pagination.pages}</span>
        </p>
        <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm">
          <button
            className="relative inline-flex cursor-pointer items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
            onClick={handleFirstPage}
            disabled={pagination.pageIndex === 0}
          >
            <ChevronDoubleLeftIcon className="h-5 w-5" />
            <span className="sr-only">First page</span>
          </button>
          <button
            className="relative inline-flex cursor-pointer items-center px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
            onClick={handlePreviousPage}
            disabled={pagination.pageIndex === 0}
          >
            <ChevronLeftIcon className="h-5 w-5" />
            <span className="sr-only">Previous page</span>
          </button>
          <button
            className="relative inline-flex cursor-pointer items-center px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
            onClick={handleNextPage}
            disabled={pagination.pageIndex >= pagination.pages - 1}
          >
            <ChevronRightIcon className="h-5 w-5" />
            <span className="sr-only">Next page</span>
          </button>
          <button
            className="relative inline-flex cursor-pointer items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
            onClick={handleLastPage}
            disabled={pagination.pageIndex >= pagination.pages - 1}
          >
            <ChevronDoubleRightIcon className="h-5 w-5" />
            <span className="sr-only">Last page</span>
          </button>
        </nav>
      </div>
    </Widget>
  );
}